import React from 'react';
import Icon from '../../../components/AppIcon';

const ClientStats = ({ clients }) => {
  const calculateStats = () => {
    const total = clients?.length;
    const active = clients?.filter(client => client?.status === 'active')?.length;
    const inactive = clients?.filter(client => client?.status === 'inactive')?.length;
    const suspended = clients?.filter(client => client?.status === 'suspended')?.length;
    
    const withActivePlans = clients?.filter(client => (client?.activePlans || 0) > 0)?.length;
    const neverTrained = clients?.filter(client => !client?.lastTraining)?.length;
    
    const today = new Date();
    const thisWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const trainedThisWeek = clients?.filter(client => {
      if (!client?.lastTraining) return false;
      return new Date(client.lastTraining) >= thisWeek;
    })?.length;
    
    const ages = clients?.map(client => {
      const birthDate = new Date(client.dateOfBirth);
      const age = today?.getFullYear() - birthDate?.getFullYear();
      return age;
    });
    
    const averageAge = ages?.length > 0 ? Math.round(ages?.reduce((sum, age) => sum + age, 0) / ages?.length) : 0;
    
    return {
      total,
      active,
      inactive,
      suspended,
      withActivePlans,
      neverTrained,
      trainedThisWeek,
      averageAge
    };
  };

  const stats = calculateStats();

  const statCards = [
    {
      title: 'Total Clients',
      value: stats?.total,
      icon: 'Users',
      color: 'text-primary bg-primary/10',
      description: 'All registered clients'
    },
    {
      title: 'Active Clients',
      value: stats?.active,
      icon: 'UserCheck',
      color: 'text-success bg-success/10',
      description: 'Currently active'
    },
    {
      title: 'With Training Plans',
      value: stats?.withActivePlans,
      icon: 'FileText',
      color: 'text-secondary bg-secondary/10',
      description: 'Have active plans'
    },
    {
      title: 'Trained This Week',
      value: stats?.trainedThisWeek,
      icon: 'Activity',
      color: 'text-wellness bg-wellness/10',
      description: 'Recent activity'
    },
    {
      title: 'Never Trained',
      value: stats?.neverTrained,
      icon: 'AlertCircle',
      color: 'text-warning bg-warning/10',
      description: 'Need attention'
    },
    {
      title: 'Average Age',
      value: `${stats?.averageAge}y`,
      icon: 'Calendar',
      color: 'text-muted-foreground bg-muted/30',
      description: 'Client demographics'
    }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      {statCards?.map((stat, index) => (
        <div
          key={index}
          className="bg-surface border border-border rounded-xl p-6 hover:shadow-md transition-all duration-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${stat?.color}`}>
              <Icon name={stat?.icon} size={24} />
            </div>
            
            {/* Status indicator for active/inactive ratio */}
            {stat?.title === 'Active Clients' && stats?.total > 0 && (
              <div className="text-right">
                <div className="text-xs text-muted-foreground">
                  {Math.round((stats?.active / stats?.total) * 100)}%
                </div>
              </div>
            )}
          </div>
          
          <div className="space-y-1">
            <div className="text-2xl font-bold text-foreground">
              {stat?.value}
            </div>
            <div className="text-sm font-medium text-foreground">
              {stat?.title}
            </div>
            <div className="text-xs text-muted-foreground">
              {stat?.description}
            </div>
          </div>
          
          {/* Additional context for specific stats */}
          {stat?.title === 'Active Clients' && stats?.inactive > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Inactive:</span>
                <span className="text-warning">{stats?.inactive}</span>
              </div>
              {stats?.suspended > 0 && (
                <div className="flex items-center justify-between text-xs mt-1">
                  <span className="text-muted-foreground">Suspended:</span>
                  <span className="text-error">{stats?.suspended}</span>
                </div>
              )}
            </div>
          )}
          
          {stat?.title === 'Never Trained' && stats?.neverTrained > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-xs text-muted-foreground">
                {Math.round((stats?.neverTrained / stats?.total) * 100)}% of total
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ClientStats;